import IconButton from "components/blocks/IconButton";
import GridView from "components/GridView";
import { GridSchema, GridViewHeaderProps } from "components/GridView/GridView.types";
import RewardConfirmationModal from "components/RewardsModals/RewardConfirmationModal";
import RewardSettingsModal from "components/RewardsModals/RewardSettingsModal";
import { useCookieContext } from "contexts";
import { useBreadcrumb } from "contexts/breadcrumb/BreadcrumbContext";
import useGridData from "hooks/useGridData";
import useSystemDateTheme from "hooks/useSystemDateTheme";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { JudgeRewardResult } from "services/judgesRewardsService/models/judgesRewardsTypes";
import {SettingKeys} from "../../../../services/settingService/models/SettingTypes";
import {RewardResponse} from "../../../../services/RewardsService/models";

type RewardConfirmationModalProps = {
  isOpen: boolean;
  data? : RewardResponse,
  name: string;
};

const JudgesRewardsGridView = () => {
  const { t } = useTranslation("Judges");
  const navigate = useNavigate();
  const { setBreadcrumbs } = useBreadcrumb();
  const { toSystemThemeDateFormat } = useSystemDateTheme();
  const { isAr } = useCookieContext();

  const [rewardConfirmationModalState, setRewardConfirmationModalState] = useState<RewardConfirmationModalProps>({
    isOpen: false,
    name: "",
  });

  const { loading, gridData, totalCount,fetchGridData, exportToExcel } = useGridData<"rewardsService", JudgeRewardResult>({
    service: "rewardsService",
    endpoint: "getAllRefereesRewards",
    exportExcelEndpoint: "exportRefereesRewardsToExcel",
    exportFileName: "refereeRewards",
    localizationNameSpace: "Judges",
  });

  useEffect(() => {
    setBreadcrumbs([
      {
        localizationKey: "judgesRewards",
        path: "/home/judgesManagement/judgeRewards",
        menuItemId: "judgesRewards",
      },
    ]);
  }, []);

  const gridSchema = useMemo<GridSchema[]>(
    () => [
      {
        field: "RefereeId",
        displayName: t("judgeName"),
        showOnMobile: true,
        sortable: true,
          filterable: true,
          filterOptions: {
              type: "serverSideSelect",
              serverSideSelectProps: {
                  label: t("judgeName"),
                  service: "accountService",
                  endpoint: "getUsersDropdown",
                  idValueKey: "id",
                  isPaginated: true,
                  textValueKey: isAr ? "arabicName" : "englishName",
                  useReactSelect: true,
              },
          },
        render: (row: RewardResponse) => {
          return isAr ? row.user?.arabicName ??"" : row.user?.englishName ??"";
        }},
      {
        field: "orderNumber",
        displayName: t("orderNumber"),
        render: (row: RewardResponse) => {
          return (
            <button className="btn btn-link" onClick={() => navigate(`/home/orders/${row.courseOrderId}`)}>
              {row.courseOrder.serialNumber}
            </button>
          );
        },
      },
      {
        field: "researchCenter",
        displayName: t("researchCenter"),
        render: (row: RewardResponse) => {
          return  row?.courseOrder?.centreManagementDetail?.displayName;
        },
      },
      {
        field: "applicantName",
        displayName: t("applicantName"),
        render: (row: RewardResponse) => {
          return isAr ? row.courseOrder?.appliedUser?.arabicName ??"" : row.courseOrder?.appliedUser?.englishName ?? "";
        },
      },
      {
          field: "ApprovalDate",
        displayName: t("rewardApprovalDate"),
          filterable: true,
          sortable: true,
          filterOptions:{
              type: "date"
          },
        render: (row: RewardResponse) => {
          return toSystemThemeDateFormat(row.approvalDate);
        },
      },
      {
        field: "Value",
          filterable: true,
        displayName: t("rewardAmount"),
        render: (row: RewardResponse) => {
          return `${row.value} ${t("Common:riyal")}`;
        },
      },
      {
        field: "RewardStatusId",
        displayName: t("Common:status"),
        showOnMobile: true,
        sortable: true,
          filterable: true,
          filterOptions: {
              type: "serverSideSelect",
              serverSideSelectProps: {
                  label: t("Common:status"),
                  service: "lookupService",
                  endpoint: "GetAllRewardStatusDropDown",
                  idValueKey:"id",
                  textValueKey:"details.0.displayName",
              }
          },
        render(row: RewardResponse) {
          return (
            <span className={row?.rewardStatus?.styleCode}>
              {row?.rewardStatus?.details?.[0]?.displayName ?? ""}
            </span>
          );
        },
      },
      {
        field: "id",
        displayName: t("Common:actions"),
        showOnMobile: true,
        render: (row: RewardResponse) => {
          return (
              <div  className={"d-flex gap-2"}>
                <IconButton
                    title={t("Common:edit")}
                    icon="icon-edit"
                    fitToIconWidth
                    innerIconColor="dark"
                    size="md"
                    onClick={() => onConfirmationModalOpen(row)}
                />

                {row?.fileId &&  <IconButton
                    title={t("paymentReceipt")}
                    icon="icon-receipt"
                    fitToIconWidth
                    innerIconColor="dark"
                    size="md"
                    onClick={() => {
                      const downloadUrl = `/api/FileManager/DownloadFile/${row.fileId}`;
                      let anchor = document.createElement("a");
                      anchor.href = downloadUrl;
                      anchor.target = "_blank";
                      anchor.download = row.file?.name?? t("paymentReceipt")
                      anchor.click();

                    }}
                />}
              </div>
          );
        },
      },
    ],
    [t, toSystemThemeDateFormat],
  );

  const viewHeader: GridViewHeaderProps = {
    title: t("judgesRewards"),
    singularName: t("Common:result"),
    totalCount: totalCount,
    onExportClick: exportToExcel,
    actionsComponent: <RewardSettingsModal settingKey={SettingKeys.RefereeRewards} />,
  };

  const onConfirmationModalOpen = (row: RewardResponse) => {
    setRewardConfirmationModalState({
      isOpen: true,
      data: row,
      name: (isAr ? row?.user?.arabicName : row?.user?.englishName) ?? "",
    });
  };

  const onConfirmationModalClose = (refetch = false) => {
    if (refetch) {
      fetchGridData();
    }
    setRewardConfirmationModalState({
      isOpen: false,
      name: ""
    });
  };

  return (
    <>
      <GridView
        loading={loading}
        gridProps={{
          data: gridData ?? [],
          gridSchema,
        }}
        viewHeaderProps={viewHeader}
      />

      <RewardConfirmationModal
        type="referee"
        isOpen={rewardConfirmationModalState.isOpen}
        onClose={onConfirmationModalClose}
        data={rewardConfirmationModalState.data}
      />
    </>
  );
};

export default JudgesRewardsGridView;
